import React, {useEffect, useState} from "react";
import {Skeleton, Box, Paper, Stack, useTheme, alpha} from "@mui/material";
import {useNavigate, useLocation} from "react-router-dom";
import CustomButton from "../.././../../core/components/ui/CustomButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CustomTypography from "../../../../core/components/ui/CustomTypography";
import CustomChipComponent from "../../../../core/components/common/custom-chip.component";
import moment from "moment";
import useGatePassConfiguration from "../../hooks/useGatePassConfiguration";


const ViewGatePassConfigurationComponent = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();

    const {isViewingGatePassConfiguration, gatePassConfigurationDetails, viewProfileGatePassConfiguration} = useGatePassConfiguration();

    const [gatepassConfigurationDetailsData, setGatepassConfigurationDetailsData] = useState(null);

    const profileGatePassConfigurationRole = new URLSearchParams(location.search).get("role");
    const profileGatePassConfigurationId = new URLSearchParams(location.search).get("id");


    useEffect(() => {
        const parameters = {
            profile_gatepass_type_uid: profileGatePassConfigurationId,
            role: profileGatePassConfigurationRole
        };

        viewProfileGatePassConfiguration(parameters);
    }, []);

    useEffect(() => {
        if (gatePassConfigurationDetails) {
            setGatepassConfigurationDetailsData(gatePassConfigurationDetails);
        }
    }, [gatePassConfigurationDetails])

  
    const handleBack = () => {
        navigate("/rbac/gatepass-configuration?role=" + gatepassConfigurationDetailsData?.role);
    }


    return (
        <>  
            <CustomButton 
                variant="text" 
                startIcon={<ArrowBackIcon fontSize="small" />}
                label="Back to Gate Pass Configuration List"
                customStyles={{fontSize: 14, ml: -2}}
                onClick={handleBack}
            />

            <Box sx={{textAlign: "start", mt: 2}}>
                <CustomTypography 
                    variant="h3"
                    text={"Gate Pass Configuration Details"}
                    customStyles={{
                        marginBottom: 0, 
                    }}
                />

                <CustomTypography 
                    variant="subtitle1"
                    text={"View profile gate pass configuration information"}
                    customStyles={{
                        marginBottom: 0, 
                    }}
                />
            </Box>

            <Paper elevation={0} sx={{border: `1px solid #e5edf6`, borderRadius: 2, padding: "24px 32px", mt: 3}}>
                <Stack direction={{xs: "column", md: "column"}} spacing={2} sx={{mb: 3}}>
                    <Stack spacing={0} flex={1}>
                        <CustomTypography 
                            variant="caption" 
                            text={"Profile Gate Pass ID"} 
                            customStyles={{
                                marginBottom: 0, 
                            }}
                        />

                        {(isViewingGatePassConfiguration) ?
                            <Skeleton variant="text" height="35px" width="34%" sx={{backgroundColor: `${alpha(theme.palette.primary.main, 0.09)}`}} />
                        :
                            <CustomTypography
                                variant="body1"
                                text={(gatePassConfigurationDetails?.profile_gatepass_type_uid)}
                                customStyles={{
                                    marginBottom: 0, 
                                    fontWeight: 600,
                                }}
                            />
                        }
                    </Stack>
                </Stack>

                <Stack direction={{xs: "column", md: "row"}} spacing={4}>
                        <Stack spacing={2} flex={1} direction={"column"}>
                            <Box>
                                <CustomTypography 
                                    variant="caption" 
                                    text={"Role"} 
                                    customStyles={{
                                        marginBottom: 0, 
                                    }}
                                />

                                {(isViewingGatePassConfiguration) ?
                                    <Skeleton variant="text" height="35px" width="70%" sx={{backgroundColor: `${alpha(theme.palette.primary.main, 0.09)}`}} />
                                :
                                    <CustomTypography
                                        variant="body1"
                                        text={(gatePassConfigurationDetails?.role)}
                                        customStyles={{
                                            marginBottom: 0, 
                                            fontWeight: 500,
                                        }}
                                    />
                                }
                            </Box>

                            <Box sx={{display: "inline-grid"}}>
                                <CustomTypography 
                                    variant="caption" 
                                    text={"Gate Pass Type"} 
                                    customStyles={{
                                        marginBottom: 0, 
                                    }}
                                />

                                {(isViewingGatePassConfiguration) ?
                                    <Skeleton variant="text" height="35px" width="70%" sx={{backgroundColor: `${alpha(theme.palette.primary.main, 0.09)}`}} />
                                :
                                    <CustomChipComponent category="gatePassType"
                                        label={(gatePassConfigurationDetails?.gatepass_type === "Single Entry Pass") ? "Single Entry" 
                                            : 
                                                (gatePassConfigurationDetails?.gatepass_type === "Multi Entry Pass") ? "Multi Entry"
                                            :
                                                (gatePassConfigurationDetails?.gatepass_type === "Annual Pass") ? "Annual"
                                            :
                                                (gatePassConfigurationDetails?.gatepass_type === "Special Entry Pass") ? "Special Entry"
                                            :
                                                gatePassConfigurationDetails?.gatepass_type
                                        }
                                    />
                                }
                            </Box>
                        </Stack>

                        <Stack spacing={2} flex={1}>
                            <Box sx={{display: "inline-grid"}}>
                                <CustomTypography 
                                    variant="caption" 
                                    text={"Entry Type"}
                                    customStyles={{
                                        marginBottom: 0, 
                                    }} 
                                />

                                {(isViewingGatePassConfiguration) ?
                                    <Skeleton variant="text" height="35px" width="70%" sx={{backgroundColor: `${alpha(theme.palette.primary.main, 0.09)}`}} />
                                :
                                    <CustomTypography
                                        variant="body1"
                                        text={gatePassConfigurationDetails?.entry_type}
                                        customStyles={{
                                            marginBottom: 0, 
                                            fontWeight: 500,
                                        }}
                                    />
                                }
                            </Box>

                            <Box>
                                <CustomTypography 
                                    variant="caption" 
                                    text={"Date Created"} 
                                    customStyles={{
                                        marginBottom: 0, 
                                    }}
                                />

                                {(isViewingGatePassConfiguration) ?
                                    <Skeleton variant="text" height="35px" width="70%" sx={{backgroundColor: `${alpha(theme.palette.primary.main, 0.09)}`}} />
                                :
                                    <CustomTypography
                                        variant="body1"
                                        text={moment(gatePassConfigurationDetails?.created_time).format("MM/DD/YYYY")}
                                        customStyles={{
                                            marginBottom: 0, 
                                            fontWeight: 500,
                                        }}
                                    />    
                                }  
                            </Box>
                        </Stack>

                        <Stack spacing={2} flex={1}>
                            <Box sx={{display: "inline-grid"}}>
                                <CustomTypography 
                                    variant="caption" 
                                    text={"Status"} 
                                    customStyles={{
                                        marginBottom: 0, 
                                    }}
                                />

                                {(isViewingGatePassConfiguration) ?
                                    <Skeleton variant="text" height="35px" width="70%" sx={{backgroundColor: `${alpha(theme.palette.primary.main, 0.09)}`}} />
                                :
                                    <CustomChipComponent category="gatePassStatus"
                                        label={(gatePassConfigurationDetails?.status === "Active") ? "Active" 
                                            : 
                                                (gatePassConfigurationDetails?.status === "Inactive") ? "Inactive"
                                            :
                                                gatePassConfigurationDetails?.status
                                        }
                                    />
                                }
                            </Box>
                        </Stack>
                </Stack>           
            </Paper>
        </>
    )
}

export default ViewGatePassConfigurationComponent